import { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import { useToast } from '../../App';

/**
 * Reusable AI Generate button.
 *
 * Props:
 *  type        — 'product' | 'order_note'
 *  label       — button label
 *  placeholder — textarea placeholder
 *  onResult    — callback(data, text) — only fields present in data are applied
 *  context     — current form state passed to backend so AI knows product type etc.
 */
const AIGenerateButton = ({
    type = 'product',
    label = '✨ Generate with AI',
    placeholder,
    onResult,
    context = {},
}) => {
    const { showError } = useToast();
    const [open, setOpen]       = useState(false);
    const [prompt, setPrompt]   = useState('');          // persists between opens
    const [loading, setLoading] = useState(false);
    const [lastApplied, setLastApplied] = useState(null); // fields applied last time
    const textareaRef = useRef(null);

    // Focus textarea when modal opens
    useEffect(() => {
        if (open && textareaRef.current) {
            setTimeout(() => textareaRef.current?.focus(), 50);
        }
    }, [open]);

    // Keyboard: Escape closes (only when not loading), Ctrl+Enter submits
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (e.key === 'Escape' && !loading) setOpen(false);
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && !loading && prompt.trim()) {
                handleGenerate();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, loading, prompt]);

    const handleGenerate = async () => {
        if (!prompt.trim()) return;
        setLoading(true);
        try {
            const res = await api.generateAIContent(type, prompt.trim(), context);
            if (res.success) {
                // Track which fields were returned so we can show the user
                const appliedFields = res.data ? Object.keys(res.data).filter(k => res.data[k] !== undefined && res.data[k] !== '') : [];
                setLastApplied(appliedFields);
                onResult && onResult(res.data, res.text);
                setOpen(false);
                // NOTE: prompt is intentionally NOT cleared — user can refine it
            } else {
                showError(res.message || 'AI generation failed.');
            }
        } catch (e) {
            showError('AI request failed.');
        } finally {
            setLoading(false);
        }
    };

    const defaultPlaceholders = {
        product:    'Describe the product, e.g. "A premium leather wallet for men, slim design, RFID blocking, dark brown, price $49.99, stock 100 units, sale price $39.99"',
        order_note: 'Describe the note, e.g. "Friendly message telling the customer their order shipped and will arrive in 3-5 days"',
    };

    const fieldLabels = {
        name: 'Product Name',
        short_description: 'Short Description',
        description: 'Description',
        regular_price: 'Regular Price',
        sale_price: 'Sale Price',
        sku: 'SKU',
        weight: 'Weight',
        stock_quantity: 'Stock Quantity',
        manage_stock: 'Manage Stock',
        tags: 'Tags',
        categories: 'Categories',
        virtual: 'Virtual',
        downloadable: 'Downloadable',
        customer_note: 'Customer Note',
    };

    return (
        <>
            <button
                type="button"
                className="btn btn-ai btn-sm"
                onClick={() => setOpen(true)}
                title="Generate content with AI"
            >
                ✨ {label}
            </button>

            {open && (
                /* No onClick on overlay — clicking outside does NOT close */
                <div className="modal-overlay ai-modal-overlay">
                    <div className="modal-content modal-medium ai-generate-modal">
                        {/* Header */}
                        <div className="modal-header">
                            <div className="ai-modal-title">
                                <span className="ai-modal-icon">✨</span>
                                <div>
                                    <h3>AI Content Generator</h3>
                                    <p className="ai-modal-subtitle">
                                        {type === 'product'
                                            ? 'Describe your product — AI fills all matching fields'
                                            : 'Describe the message — AI writes it for you'}
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="modal-close"
                                onClick={() => !loading && setOpen(false)}
                                disabled={loading}
                                title="Close"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Body */}
                        <div className="modal-body">
                            {/* Context hint for product type */}
                            {type === 'product' && context.type && (
                                <div className="ai-context-badge">
                                    <span>Product type: <strong>{context.type}</strong></span>
                                    {context.virtual     && <span className="badge-tag">Virtual</span>}
                                    {context.downloadable && <span className="badge-tag">Downloadable</span>}
                                </div>
                            )}

                            {/* Fields hint */}
                            <div className="ai-fields-hint">
                                {type === 'product' ? (
                                    <>
                                        <strong>Fields AI can fill:</strong>{' '}
                                        Name, Short Description, Description, Regular Price, Sale Price, SKU, Weight, Stock Quantity, Tags, Categories
                                        {context.virtual && ', Virtual'}
                                        {context.downloadable && ', Downloadable'}
                                        <br />
                                        <span className="hint-tip">💡 Tip: mention price, sale price, stock, weight in your prompt to fill those fields too.</span>
                                    </>
                                ) : (
                                    <span className="hint-tip">💡 AI will write a professional customer message based on your description.</span>
                                )}
                            </div>

                            {/* Last applied fields */}
                            {lastApplied && lastApplied.length > 0 && (
                                <div className="ai-last-applied">
                                    <span className="applied-label">✅ Last applied:</span>
                                    {lastApplied.map(f => (
                                        <span key={f} className="applied-tag">{fieldLabels[f] || f}</span>
                                    ))}
                                    <span className="applied-refine">Refine your prompt below to update specific fields.</span>
                                </div>
                            )}

                            {/* Prompt textarea */}
                            <textarea
                                ref={textareaRef}
                                className="form-control ai-prompt-textarea"
                                rows={4}
                                placeholder={placeholder || defaultPlaceholders[type]}
                                value={prompt}
                                onChange={e => setPrompt(e.target.value)}
                                disabled={loading}
                            />
                            <p className="ai-keyboard-hint">Press <kbd>Ctrl+Enter</kbd> to generate</p>
                        </div>

                        {/* Footer */}
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setOpen(false)}
                                disabled={loading}
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                className={`btn btn-ai ${loading ? 'btn-loading' : ''}`}
                                onClick={handleGenerate}
                                disabled={loading || !prompt.trim()}
                            >
                                {loading
                                    ? <><span className="spinner-sm"></span> Generating…</>
                                    : '✨ Generate'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AIGenerateButton;
